import * as React from 'react';

import { OtherPhotoCard, PhotoGrid } from '../../loads/LoadPhotos';

import { GenericFile, UploadErrorActions } from '~/modules/trade/types/file';

type ScaleTicketPhotoGridProps = {
  files: GenericFile[];
  uploadErrorActions: UploadErrorActions;
  downloadFile: (file: GenericFile) => void;
  isGeneratingDownloadUrl: boolean;
  onDeletePhoto: (id: string) => void;
  onRetryDelete: (id: string) => void;
  onDismissOptimisticError: (id: string) => void;
  onView?: (fileId: string) => void;
};

const resolveHandler = (
  hasUploadError: boolean,
  hasOptimisticError: boolean,
  handlers: { onUploadError: () => void; onOptimisticError: () => void },
) => {
  if (hasUploadError) return handlers.onUploadError;
  if (hasOptimisticError) return handlers.onOptimisticError;
  return undefined;
};

export const ScaleTicketPhotoGrid: React.FC<ScaleTicketPhotoGridProps> = ({
  files,
  uploadErrorActions,
  downloadFile,
  isGeneratingDownloadUrl,
  onDeletePhoto,
  onRetryDelete,
  onDismissOptimisticError,
  onView,
}) => (
  <PhotoGrid className="@ScaleTicketPhotoGrid">
    {files.map((file) => {
      const hasUploadError = file.uploadState === 'error';
      const hasOptimisticError = file._optimistic?.status === 'error';

      const handleRetry = resolveHandler(hasUploadError, hasOptimisticError, {
        onUploadError: () => uploadErrorActions.retryUpload(file.id),
        onOptimisticError: () => onRetryDelete(file.id),
      });

      const handleDismiss = resolveHandler(hasUploadError, hasOptimisticError, {
        onUploadError: () => uploadErrorActions.dismissError(file.id),
        onOptimisticError: () => onDismissOptimisticError(file.id),
      });

      return (
        <OtherPhotoCard
          key={file.id}
          file={file}
          onView={file.downloadUrl ? () => onView?.(file.id) : undefined}
          onDownload={() => downloadFile(file)}
          onDelete={() => onDeletePhoto(file.id)}
          onRetry={handleRetry}
          onDismissError={handleDismiss}
          isDownloading={isGeneratingDownloadUrl}
        />
      );
    })}
  </PhotoGrid>
);
